import { Component, inject, signal } from '@angular/core';
import {
  FormBuilder,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogClose,
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { AxiosError } from 'axios';
import QRCode from 'qrcode';
import { AuthService } from 'src/app/shared/services/auth/auth.service';
import { UsersService } from 'src/app/services/users/users.service';
import { PayeePopupComponent } from '../../../shared/payee-popup/payee-popup.component';
import { PayeeOtpFormFieldComponent } from '../../../shared/payee-form-fields/payee-otp-form-field/payee-otp-form-field.component';
import { MatError } from '@angular/material/form-field';
import { PayeeButtonComponent } from '../../../shared/payee-buttons/payee-button/payee-button.component';

export type TwoFactorDialogData = {
  has2FA: boolean;
};

type Step = 'confirm' | 'code';

@Component({
  selector: 'app-two-factor-dialog',
  templateUrl: './two-factor-dialog.component.html',
  styleUrls: ['./two-factor-dialog.component.scss'],
  imports: [
    PayeePopupComponent,
    FormsModule,
    ReactiveFormsModule,
    PayeeOtpFormFieldComponent,
    MatError,
    PayeeButtonComponent,
    MatDialogClose,
    TranslateModule,
  ],
})
export class TwoFactorDialogComponent {
  private fb = inject(FormBuilder);
  private authService = inject(AuthService);
  private usersService = inject(UsersService);
  private snackbar = inject(MatSnackBar);
  private translate = inject(TranslateService);
  private dialogRef =
    inject<MatDialogRef<TwoFactorDialogComponent, boolean>>(MatDialogRef);
  private data = inject<TwoFactorDialogData>(MAT_DIALOG_DATA);

  readonly has2FA = this.data.has2FA;

  readonly step = signal<Step>('confirm');
  readonly loading = signal(false);
  readonly qrCodeDataUrl = signal<string | null>(null);
  readonly secret = signal<string | null>(null);

  readonly codeForm = this.fb.group({
    code: this.fb.control('', {
      validators: [Validators.required, Validators.pattern(/^\d{6}$/)],
      nonNullable: true,
    }),
  });

  get code() {
    return this.codeForm.controls.code;
  }

  get titleKey(): string {
    const action = this.has2FA ? 'DISABLE' : 'ENABLE';
    return `TWO_FACTOR_DIALOG.${action}.TITLE`;
  }

  get descriptionKey(): string {
    const action = this.has2FA ? 'DISABLE' : 'ENABLE';
    const view = this.step() === 'code' ? 'CODE_DESCRIPTION' : 'DESCRIPTION';
    return `TWO_FACTOR_DIALOG.${action}.${view}`;
  }

  get primaryColor(): string {
    return this.has2FA ? 'alert-red' : 'blue-payee';
  }

  async onPrimary(): Promise<void> {
    if (this.loading()) {
      return;
    }

    if (this.has2FA) {
      this.step.set('code');
      return;
    }

    try {
      this.loading.set(true);
      const result = await this.authService.register2FA();
      this.secret.set(result.secret);
      this.qrCodeDataUrl.set(await QRCode.toDataURL(result.otpauth_url, {
        margin: 1,
        width: 200,
      }));
      this.step.set('code');
    } catch (error) {
      this.showError(error);
    } finally {
      this.loading.set(false);
    }
  }

  async onSubmitCode(): Promise<void> {
    if (this.loading() || this.codeForm.invalid) {
      this.codeForm.markAllAsTouched();
      return;
    }

    const { code } = this.codeForm.getRawValue();

    try {
      this.loading.set(true);
      await this.authService.verify2FACode(code);

      if (this.has2FA) {
        await this.authService.disable2FA();
      }

      await this.usersService.setUser();

      this.snackbar.open(
        this.translate.instant(
          this.has2FA
            ? 'TWO_FACTOR_DIALOG.DISABLE.SUCCESS'
            : 'TWO_FACTOR_DIALOG.ENABLE.SUCCESS',
        ),
        this.translate.instant('OK'),
        { duration: 5000 },
      );
      this.dialogRef.close(true);
    } catch (error) {
      this.showError(error);
    } finally {
      this.loading.set(false);
    }
  }

  private showError(error: unknown): void {
    console.error('Error during MFA operation', error);
    if (error instanceof AxiosError && error.response?.status === 422) {
      this.code.setErrors({ invalidCode: true });
      return;
    }
    this.snackbar.open(
      this.translate.instant('ERROR_MESSAGES.UNKNOWN_ERROR'),
      this.translate.instant('OK'),
      { duration: 5000 },
    );
  }
}
